import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["item"];
  static values = {
    classes: { type: Array, default: ["hidden"] },
    persistent: { type: Boolean, default: false },
    storageKey: { type: String, default: "toggle-state" },
    toggles: { type: Object, default: {} },
    persistentKeys: { type: Object, default: {} },
  };

  connect() {
    // Load default toggle state if persistent
    if (this.persistentValue) {
      this._loadState();
    }

    // Load keyed toggle states
    Object.keys(this.togglesValue).forEach((key) => {
      if (this.persistentKeysValue[key]) {
        this._loadState(key);
      }
    });
  }

  _getClasses(key) {
    return key ? this.togglesValue[key] : this.classesValue;
  }

  _getStorageKey(key) {
    return key ? `toggle-${key}` : this.storageKeyValue;
  }

  _isPersistent(key) {
    return key ? this.persistentKeysValue[key] : this.persistentValue;
  }

  _call(method, key = null) {
    const classes = this._getClasses(key);
    if (!classes) return;

    const targets = this.hasItemTarget ? this.itemTargets : [this.element];
    targets.forEach((target) => {
      classes.forEach((cls) => target.classList[method](cls));
    });

    if (this._isPersistent(key)) {
      this._saveState(key);
    }
  }

  _loadState(key = null) {
    try {
      const storageKey = this._getStorageKey(key);
      const state = localStorage.getItem(storageKey);
      if (state !== null) {
        const isActive = JSON.parse(state);
        this._call(isActive ? "add" : "remove", key);
      }
    } catch (error) {
      // Silently fail - localStorage unavailable (private browsing, disabled, etc.)
      console.debug("Toggle state loading failed:", error.message);
    }
  }

  _saveState(key = null) {
    try {
      const classes = this._getClasses(key);
      const targets = this.hasItemTarget ? this.itemTargets : [this.element];
      const isActive = targets.some((target) =>
        classes.some((cls) => target.classList.contains(cls)),
      );
      const storageKey = this._getStorageKey(key);
      localStorage.setItem(storageKey, JSON.stringify(isActive));
    } catch (error) {
      // Silently fail - localStorage unavailable (private browsing, disabled, quota exceeded, etc.)
      console.debug("Toggle state saving failed:", error.message);
    }
  }

  toggle(event) {
    const key = event?.params?.key;
    this._call("toggle", key);
  }

  on(event) {
    const key = event?.params?.key;
    this._call("add", key);
  }

  off(event) {
    const key = event?.params?.key;
    this._call("remove", key);
  }
}
